"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { Category } from "@/types/models/category";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Store, Trash2 } from "lucide-react";

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "DRAFT":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "INACTIVE":
      return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const formatStatus = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "DRAFT":
      return "Draft";
    case "INACTIVE":
      return "Inactive";
    default:
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

export const columns: ColumnDef<Category>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div
          className="flex items-center justify-center transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const isSelected = row.getIsSelected();
      const category = row.original;
      // Don't show checkbox if already mapped
      if (category.already_mapped) {
        return null;
      }

      return (
        <div
          className={`flex items-center justify-center transition-opacity duration-200 ${
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    size: 280,
    minSize: 220,
    maxSize: 350,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Collection</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const category = row.original;
      const date = category.updated_at
        ? new Date(category.updated_at)
        : new Date();
      const formattedDate = format(date, "MMM dd yyyy");

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            {category.imageAssetId ? (
              <img
                src={getAsset(category.imageAssetId, {
                  width: 60,
                  height: 60,
                  fit: "cover",
                })}
                alt={category.title}
                className="w-10 h-10 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = getAsset(
                    "seller/b66f0c3b-54dc-4564-af3a-848196817901/products/images/bljmuedorxDSsvOtpgQP.jpg",
                    { width: 60, height: 60, fit: "cover" },
                  );
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                <Store className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold truncate max-w-[200px]"
              title={category.title}
            >
              {category.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              {(category.total_products !== undefined || category.products) && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground">
                    {category.total_products !== undefined
                      ? category.total_products
                      : (Array.isArray(category.products)
                          ? category.products.length
                          : 0)}{" "}
                    products
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "total_products",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Products</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const category = row.original;
      // Use total_products if available, otherwise fallback to products array length
      const productCount = category.total_products !== undefined
        ? category.total_products
        : (Array.isArray(category.products) ? category.products.length : 0);

      return (
        <div>
          <h3 className="text-xs font-semibold">{productCount}</h3>
          <span className="text-xs text-gray-600">
            {productCount === 1 ? "product" : "products"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.original.status || "INACTIVE";
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "already_mapped",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Import Status</span>,
    cell: ({ row }) => {
      const alreadyMapped = row.original.already_mapped;
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${
            alreadyMapped
              ? "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20"
              : "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20"
          }`}
        >
          {alreadyMapped ? "Imported" : "Not Imported"}
        </Badge>
      );
    },
  },
  {
    id: "actions",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row, table }) => {
      const category = row.original;
      const onUnmap = (table.options.meta as any)?.onUnmap;

      // Only show unmap button if already mapped
      if (!category.already_mapped || !onUnmap) {
        return null;
      }

      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onUnmap(category)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Unmap
          </Button>
        </div>
      );
    },
  },
];
